/**
 * Utility for handling in-app notifications and service worker management
 */

// Cache for installation status to avoid repeated checks
let appInstalledStatus: boolean | null = null;
let lastCheckTime = 0;
const CHECK_INTERVAL = 60000; // 1 minute between checks

/**
 * Checks if the service worker is active and responding
 */
export const checkServiceWorkerActive = async (): Promise<boolean> => {
  if (!('serviceWorker' in navigator)) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('This browser does not support Service Workers');
    }
    return false;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const isActive = registrations.length > 0 && registrations.some(reg => reg.active);
    
    if (process.env.NODE_ENV !== 'production') {
      console.log(`Service Worker status: ${isActive ? 'Active' : 'Inactive'}`);
    }
    
    return isActive;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Error checking Service Worker status:', error);
    }
    return false;
  }
};

/**
 * Registers or updates the service worker
 */
export const registerServiceWorker = async (): Promise<boolean> => {
  if (!('serviceWorker' in navigator)) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('This browser does not support Service Workers');
    }
    return false;
  }

  try {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Registering Service Worker...');
    }
    
    const registration = await navigator.serviceWorker.register('/sw.js', {
      updateViaCache: 'none'
    });
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('Service Worker registered successfully:', registration);
    }
    
    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    
    return true;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Error registering Service Worker:', error);
    }
    return false;
  }
};

/**
 * Force update of the service worker
 */
export const forceServiceWorkerUpdate = async (): Promise<boolean> => {
  if (!('serviceWorker' in navigator)) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('This browser does not support Service Workers');
    }
    return false;
  }

  try {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Forcing Service Worker update...');
    }
    
    // Unregister all existing service workers
    const registrations = await navigator.serviceWorker.getRegistrations();
    
    for (const registration of registrations) {
      await registration.unregister();
      if (process.env.NODE_ENV !== 'production') {
        console.log('Service Worker successfully unregistered');
      }
    }
    
    // Register a new service worker
    const newRegistration = await navigator.serviceWorker.register('/sw.js', {
      updateViaCache: 'none'
    });
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('New Service Worker registered:', newRegistration);
    }
    
    // Wait for it to be ready
    await navigator.serviceWorker.ready;
    
    return true;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Error forcing Service Worker update:', error);
    }
    return false;
  }
};

/**
 * Check if the app is already installed as a PWA
 * Uses cache to avoid frequent recalculations
 */
export const isAppInstalled = (): boolean => {
  const now = Date.now();
  
  // If we have a cached value and less than a minute has passed, use that value
  if (appInstalledStatus !== null && (now - lastCheckTime) < CHECK_INTERVAL) {
    return appInstalledStatus;
  }
  
  // Check display modes
  const standalone = window.matchMedia('(display-mode: standalone)').matches;
  const fullScreen = window.matchMedia('(display-mode: fullscreen)').matches;
  const minimal = window.matchMedia('(display-mode: minimal-ui)').matches;
  
  // Also check if it's on iOS and was added to the home screen
  const isIOSInStandaloneMode = () => {
    return 'standalone' in navigator && (navigator as any).standalone === true;
  };
  
  // Log only during development and only when doing a fresh check
  if (process.env.NODE_ENV !== 'production' && appInstalledStatus === null) {
    console.log("Installation status check:", { 
      standalone, 
      fullScreen, 
      minimal, 
      iosStandalone: 'standalone' in navigator ? (navigator as any).standalone : undefined 
    });
  }
  
  // Save the result and last check time
  appInstalledStatus = standalone || fullScreen || minimal || isIOSInStandaloneMode();
  lastCheckTime = now;
  
  return appInstalledStatus;
};

/**
 * Reset the cache for installation status (useful when app gets installed)
 */
export const resetInstallationCache = (): void => {
  appInstalledStatus = null;
  lastCheckTime = 0;
};

/**
 * Check if browser supports PWA installation
 */
export const isPwaInstallable = (): boolean => {
  const supportsServiceWorker = 'serviceWorker' in navigator;
  const isSecureContext = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
  const hasManifest = !!document.querySelector('link[rel="manifest"]');
  
  // Log this only in development
  if (process.env.NODE_ENV === 'development') {
    console.log("PWA compatibility:", {
      supportsServiceWorker,
      isSecureContext,
      hasManifest
    });
  }
  
  return supportsServiceWorker && isSecureContext && hasManifest;
};

/**
 * Test push notification functionality
 */
export const testPushNotification = async (): Promise<boolean> => {
  if (!('Notification' in window)) {
    console.error('This browser does not support notifications');
    return false;
  }

  if (Notification.permission !== 'granted') {
    console.error('Notification permission not granted');
    return false;
  }

  try {
    // Show a test notification
    const notification = new Notification('Test de notificaciones', {
      body: 'Las notificaciones están funcionando correctamente',
      icon: '/lovable-uploads/a4ae9111-3205-4bc9-b225-14acafe7855c.png',
      badge: '/lovable-uploads/a4ae9111-3205-4bc9-b225-14acafe7855c.png',
      tag: 'test-notification'
    });

    // Auto close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);

    return true;
  } catch (error) {
    console.error('Error showing test notification:', error);
    return false;
  }
};

// Keep compatibility with existing code by providing stub functions
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('Use usePushNotifications hook for push notification management');
  }
  return true;
};

export const scheduleNutritionReminder = async (timeString: string, message: string): Promise<string> => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('Scheduled reminders are temporarily disabled. Use in-app notifications instead.');
  }
  return 'reminder-disabled';
};

export const listScheduledReminders = async (): Promise<any[]> => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('Scheduled reminders are temporarily disabled.');
  }
  return [];
};

export const cancelScheduledReminder = async (id: string): Promise<boolean> => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('Scheduled reminders are temporarily disabled.');
  }
  return true;
};
